/**
 * Team Pace — how fast each team moved through the map.
 *
 * A team that never finished shows "DNF" and a blank total, never an elapsed
 * time: the snapshot leaves `totalElapsedMs` null for them precisely so this
 * card cannot imply they were still racing when the game was called.
 */

import type { TrlTeamStat } from '@swtorbit/shared-socket-events';
import { StatCard } from './StatCard';
import { formatMs, ordinal } from './statsFormat';

export function TrlPaceCard({ teams }: { teams: TrlTeamStat[] }) {
  return (
    <StatCard
      title="Team Pace"
      icon="⏱️"
      summary={`${teams.length} teams`}
      isEmpty={teams.length === 0}
      emptyMessage="No teams in this game."
    >
      <div className="d-flex flex-column gap-2xs">
        <div className="d-flex gap-xs fs-xs text-slate px-xs">
          <span className="flex-1">Team</span>
          <span className="w-auto" style={{ minWidth: '5rem' }}>Planets</span>
          <span style={{ minWidth: '5rem' }}>Fastest</span>
          <span style={{ minWidth: '5rem' }}>Average</span>
          <span style={{ minWidth: '5rem' }}>Slowest</span>
          <span style={{ minWidth: '6rem' }}>Total</span>
        </div>

        {teams.map((team) => (
          <div
            key={team.teamId}
            className="d-flex gap-xs align-items-center px-xs py-2xs rounded-2xs"
            style={{ background: 'color-mix(in srgb, var(--color-white) 4%, transparent)' }}
          >
            <span className="flex-1 d-flex align-items-center gap-xs">
              {team.color && (
                <span
                  className="rounded-2xs"
                  style={{ width: '10px', height: '10px', background: team.color, flexShrink: 0 }}
                />
              )}
              <span className="text-white">{team.name}</span>
              <span className="fs-xs text-slate">{ordinal(team.placement)}</span>
            </span>
            <span className="fs-sm text-cyan" style={{ minWidth: '5rem' }}>
              {team.planetsCleared}
              {team.firstPlaceFinishes > 0 && (
                <span className="fs-xs text-gold"> · {team.firstPlaceFinishes}× 1st</span>
              )}
            </span>
            <span className="fs-sm text-cyan" style={{ minWidth: '5rem' }}>{formatMs(team.fastestClearMs)}</span>
            <span className="fs-sm text-cyan" style={{ minWidth: '5rem' }}>{formatMs(team.averageClearMs)}</span>
            <span className="fs-sm text-cyan" style={{ minWidth: '5rem' }}>{formatMs(team.slowestClearMs)}</span>
            <span className="fs-sm" style={{ minWidth: '6rem' }}>
              {team.finished ? (
                <span className="text-white">{formatMs(team.totalElapsedMs)}</span>
              ) : (
                <span className="badge badge--sm badge--red">DNF</span>
              )}
            </span>
          </div>
        ))}
      </div>
    </StatCard>
  );
}
